import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import FileUploader from './FileUploader'

function ContentManager() {
  const [activeTab, setActiveTab] = useState('general')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [mediaItems, setMediaItems] = useState([])
  const [selectedImageField, setSelectedImageField] = useState('')
  const [sectionVisibility, setSectionVisibility] = useState({
    hero: true,
    services: true,
    solutions: true,
    about: true,
    testimonials: true,
    contact: true
  })
  
  // General settings
  const [generalContent, setGeneralContent] = useState({
    logo: {
      text: 'CodeVaro',
      highlight: '.eu',
      image_url: ''
    },
    favicon: '',
    contact: {
      phone: '28210 123456',
      email: 'info@codevaro.eu',
      address: 'Χανιά, Κρήτη',
      hours: 'Δευ-Παρ: 09:00-17:00'
    },
    social_media: {
      facebook: 'https://facebook.com/codevaro',
      instagram: 'https://instagram.com/codevaro',
      linkedin: 'https://linkedin.com/company/codevaro',
      twitter: 'https://twitter.com/codevaro'
    }
  })
  
  // Hero section content
  const [heroContent, setHeroContent] = useState({
    title: 'Καινοτόμες Λύσεις Πληροφορικής',
    subtitle: 'Εξειδικευμένες υπηρεσίες web development και digital marketing για την επιχείρησή σας',
    cta_text: 'Επικοινωνήστε μαζί μας',
    cta_link: '#contact',
    background_url: '',
    visible: true
  })
  
  // About section content
  const [aboutContent, setAboutContent] = useState({
    title: 'Σχετικά με εμάς',
    content: 'Η CodeVaro είναι μια εταιρεία πληροφορικής που εξειδικεύεται στην ανάπτυξη λύσεων web και mobile. Με πάνω από 10 χρόνια εμπειρίας, προσφέρουμε καινοτόμες λύσεις που βοηθούν τις επιχειρήσεις να αναπτυχθούν ψηφιακά.',
    image_url: '',
    visible: true
  })
  
  // Footer content
  const [footerContent, setFooterContent] = useState({
    copyright: '© 2023 CodeVaro. Με επιφύλαξη παντός δικαιώματος.',
    links: [
      { text: 'Όροι Χρήσης', url: '/terms' },
      { text: 'Πολιτική Απορρήτου', url: '/privacy' }
    ]
  })
  
  useEffect(() => {
    fetchContent()
  }, [])
  
  const fetchContent = async () => {
    try {
      setLoading(true)
      
      // Fetch settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('codevaro_settings')
        .select('setting_key, value')
      
      if (settingsError) throw settingsError
      
      // Process settings
      settingsData.forEach(item => {
        if (item.setting_key === 'general_content') {
          setGeneralContent(item.value)
        } else if (item.setting_key === 'hero_content') {
          setHeroContent(item.value)
        } else if (item.setting_key === 'about_content') {
          setAboutContent(item.value)
        } else if (item.setting_key === 'footer_content') {
          setFooterContent(item.value)
        } else if (item.setting_key === 'section_visibility') {
          setSectionVisibility(item.value)
        }
      })
    } catch (error) {
      console.error('Error fetching content:', error)
      setError('Σφάλμα κατά τη φόρτωση του περιεχομένου')
    } finally {
      setLoading(false)
    }
  }
  
  const handleGeneralChange = (section, field, value) => {
    setGeneralContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }
  
  const handleLogoTextChange = (field, value) => {
    setGeneralContent(prev => ({
      ...prev,
      logo: {
        ...prev.logo,
        [field]: value
      }
    }))
  }
  
  const handleHeroChange = (field, value) => {
    setHeroContent(prev => ({
      ...prev,
      [field]: value
    }))
  }
  
  const handleAboutChange = (field, value) => {
    setAboutContent(prev => ({
      ...prev,
      [field]: value
    }))
  }
  
  const handleFooterChange = (field, value) => {
    if (field === 'copyright') {
      setFooterContent(prev => ({
        ...prev,
        copyright: value
      }))
    }
  }
  
  const handleFooterLinkChange = (index, field, value) => {
    setFooterContent(prev => {
      const newLinks = [...prev.links]
      newLinks[index] = {
        ...newLinks[index],
        [field]: value
      }
      return {
        ...prev,
        links: newLinks
      }
    })
  }
  
  const addFooterLink = () => {
    setFooterContent(prev => ({
      ...prev,
      links: [
        ...prev.links,
        { text: '', url: '' }
      ]
    }))
  }
  
  const removeFooterLink = (index) => {
    setFooterContent(prev => {
      const newLinks = [...prev.links]
      newLinks.splice(index, 1)
      return {
        ...prev,
        links: newLinks
      }
    })
  }

  const handleSectionVisibilityChange = (section) => {
    setSectionVisibility(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const fetchMediaItems = async () => {
    try {
      setLoading(true)
      
      // Get files from storage
      const { data: storageData, error: storageError } = await supabase
        .storage
        .from('images')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' }
        })
      
      if (storageError) throw storageError
      
      // Get public URLs for each file
      const mediaItems = await Promise.all(
        storageData.map(async (item) => {
          const { data: publicUrlData } = await supabase
            .storage
            .from('images')
            .getPublicUrl(item.name)
          
          return {
            ...item,
            publicUrl: publicUrlData.publicUrl
          }
        })
      )
      
      setMediaItems(mediaItems)
    } catch (error) {
      console.error('Error fetching media items:', error)
      setError('Σφάλμα κατά τη φόρτωση των αρχείων πολυμέσων')
    } finally {
      setLoading(false)
    }
  }

  const openMediaLibrary = (fieldName) => {
    setSelectedImageField(fieldName)
    fetchMediaItems()
    setIsMediaLibraryOpen(true)
  }

  const selectMedia = (url) => {
    // Update the appropriate field based on which one was selected
    if (selectedImageField === 'logo') {
      setGeneralContent(prev => ({
        ...prev,
        logo: {
          ...prev.logo,
          image_url: url
        }
      }))
    } else if (selectedImageField === 'favicon') {
      setGeneralContent(prev => ({
        ...prev,
        favicon: url
      }))
    } else if (selectedImageField === 'hero_background') {
      setHeroContent(prev => ({
        ...prev,
        background_url: url
      }))
    } else if (selectedImageField === 'about_image') {
      setAboutContent(prev => ({
        ...prev,
        image_url: url
      }))
    }
    
    setIsMediaLibraryOpen(false)
  }

  const handleFileUploaded = (url) => {
    // Update the appropriate field based on which one was selected
    if (selectedImageField === 'logo') {
      setGeneralContent(prev => ({
        ...prev,
        logo: {
          ...prev.logo,
          image_url: url
        }
      }))
    } else if (selectedImageField === 'favicon') {
      setGeneralContent(prev => ({
        ...prev,
        favicon: url
      }))
    } else if (selectedImageField === 'hero_background') {
      setHeroContent(prev => ({
        ...prev,
        background_url: url
      }))
    } else if (selectedImageField === 'about_image') {
      setAboutContent(prev => ({
        ...prev,
        image_url: url
      }))
    }
  }
  
  const saveContent = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(null)
      
      // Save general content
      const { error: generalError } = await supabase
        .from('codevaro_settings')
        .upsert({
          setting_key: 'general_content',
          value: generalContent,
          updated_at: new Date()
        }, { onConflict: 'setting_key' })
      
      if (generalError) throw generalError
      
      // Save hero content
      const { error: heroError } = await supabase
        .from('codevaro_settings')
        .upsert({
          setting_key: 'hero_content',
          value: heroContent,
          updated_at: new Date()
        }, { onConflict: 'setting_key' })
      
      if (heroError) throw heroError
      
      // Save about content
      const { error: aboutError } = await supabase
        .from('codevaro_settings')
        .upsert({
          setting_key: 'about_content',
          value: aboutContent,
          updated_at: new Date()
        }, { onConflict: 'setting_key' })
      
      if (aboutError) throw aboutError
      
      // Save footer content
      const { error: footerError } = await supabase
        .from('codevaro_settings')
        .upsert({
          setting_key: 'footer_content',
          value: footerContent,
          updated_at: new Date()
        }, { onConflict: 'setting_key' })
      
      if (footerError) throw footerError
      
      // Save section visibility
      const { error: visibilityError } = await supabase
        .from('codevaro_settings')
        .upsert({
          setting_key: 'section_visibility',
          value: sectionVisibility,
          updated_at: new Date()
        }, { onConflict: 'setting_key' })
      
      if (visibilityError) throw visibilityError
      
      setSuccess('Το περιεχόμενο αποθηκεύτηκε επιτυχώς')
      setTimeout(() => setSuccess(null), 5000)
    } catch (error) {
      console.error('Error saving content:', error)
      setError('Σφάλμα κατά την αποθήκευση του περιεχομένου')
    } finally {
      setLoading(false)
    }
  }
  
  const renderTab = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Γενικές Ρυθμίσεις</h3>
              <p className="mt-1 text-sm text-gray-500">
                Βασικές ρυθμίσεις για την ιστοσελίδα.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Λογότυπο</h4>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="logo_text" className="block text-sm font-medium text-gray-700">
                    Κείμενο Λογότυπου
                  </label>
                  <input
                    type="text"
                    id="logo_text"
                    value={generalContent.logo.text}
                    onChange={(e) => handleLogoTextChange('text', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="logo_highlight" className="block text-sm font-medium text-gray-700">
                    Επισήμανση (π.χ. ".eu")
                  </label>
                  <input
                    type="text"
                    id="logo_highlight"
                    value={generalContent.logo.highlight}
                    onChange={(e) => handleLogoTextChange('highlight', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6">
                  <label htmlFor="logo_image" className="block text-sm font-medium text-gray-700">
                    Εικόνα Λογότυπου (προαιρετικό)
                  </label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="text"
                      id="logo_image"
                      value={generalContent.logo.image_url}
                      onChange={(e) => handleLogoTextChange('image_url', e.target.value)}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      placeholder="https://example.com/logo.png"
                    />
                    <button
                      type="button"
                      onClick={() => openMediaLibrary('logo')}
                      className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <SafeIcon icon={FiIcons.FiFolder} className="mr-1" />
                      Επιλογή
                    </button>
                  </div>
                  
                  {generalContent.logo.image_url && (
                    <div className="mt-2">
                      <div className="flex items-center mt-2">
                        <img 
                          src={generalContent.logo.image_url} 
                          alt="Λογότυπο" 
                          className="h-12 object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => handleLogoTextChange('image_url', '')}
                          className="ml-2 text-red-600 hover:text-red-900"
                        >
                          <SafeIcon icon={FiIcons.FiX} className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Μεταφόρτωση νέας εικόνας λογότυπου
                    </label>
                    <FileUploader 
                      onFileUploaded={(url) => handleFileUploaded(url)}
                      acceptedFileTypes="image/png,image/jpeg,image/svg+xml"
                      maxSizeMB={2}
                    />
                  </div>
                </div>
                
                <div className="col-span-6">
                  <label htmlFor="favicon" className="block text-sm font-medium text-gray-700">
                    Favicon
                  </label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="text"
                      id="favicon"
                      value={generalContent.favicon}
                      onChange={(e) => setGeneralContent({...generalContent, favicon: e.target.value})}
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      placeholder="https://example.com/favicon.ico"
                    />
                    <button
                      type="button"
                      onClick={() => openMediaLibrary('favicon')}
                      className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <SafeIcon icon={FiIcons.FiFolder} className="mr-1" />
                      Επιλογή
                    </button>
                  </div>
                  
                  {generalContent.favicon && (
                    <div className="flex items-center mt-2">
                      <img 
                        src={generalContent.favicon} 
                        alt="Favicon" 
                        className="h-8 w-8 object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => setGeneralContent({...generalContent, favicon: ''})}
                        className="ml-2 text-red-600 hover:text-red-900"
                      >
                        <SafeIcon icon={FiIcons.FiX} className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                  
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Μεταφόρτωση νέου favicon
                    </label>
                    <FileUploader 
                      onFileUploaded={(url) => handleFileUploaded(url)}
                      acceptedFileTypes="image/png,image/jpeg,image/x-icon,image/svg+xml"
                      maxSizeMB={1}
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 border border-gray-200 rounded-md">
                <p className="text-sm font-medium text-gray-700 mb-2">Προεπισκόπηση Λογότυπου:</p>
                <div className="text-2xl font-bold text-blue-600">
                  {generalContent.logo.text}<span className="text-orange-500">{generalContent.logo.highlight}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Στοιχεία Επικοινωνίας</h4>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                    Τηλέφωνο
                  </label>
                  <input
                    type="text"
                    id="contact_phone"
                    value={generalContent.contact.phone}
                    onChange={(e) => handleGeneralChange('contact', 'phone', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    value={generalContent.contact.email}
                    onChange={(e) => handleGeneralChange('contact', 'email', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="contact_address" className="block text-sm font-medium text-gray-700">
                    Διεύθυνση
                  </label>
                  <input
                    type="text"
                    id="contact_address"
                    value={generalContent.contact.address}
                    onChange={(e) => handleGeneralChange('contact', 'address', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="contact_hours" className="block text-sm font-medium text-gray-700">
                    Ωράριο Λειτουργίας
                  </label>
                  <input
                    type="text"
                    id="contact_hours"
                    value={generalContent.contact.hours}
                    onChange={(e) => handleGeneralChange('contact', 'hours', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Κοινωνικά Δίκτυα</h4>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="social_facebook" className="block text-sm font-medium text-gray-700">
                    Facebook URL
                  </label>
                  <input
                    type="text"
                    id="social_facebook"
                    value={generalContent.social_media.facebook}
                    onChange={(e) => handleGeneralChange('social_media', 'facebook', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="social_instagram" className="block text-sm font-medium text-gray-700">
                    Instagram URL
                  </label>
                  <input
                    type="text"
                    id="social_instagram"
                    value={generalContent.social_media.instagram}
                    onChange={(e) => handleGeneralChange('social_media', 'instagram', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="social_linkedin" className="block text-sm font-medium text-gray-700">
                    LinkedIn URL
                  </label>
                  <input
                    type="text"
                    id="social_linkedin"
                    value={generalContent.social_media.linkedin}
                    onChange={(e) => handleGeneralChange('social_media', 'linkedin', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="social_twitter" className="block text-sm font-medium text-gray-700">
                    Twitter URL
                  </label>
                  <input
                    type="text"
                    id="social_twitter"
                    value={generalContent.social_media.twitter}
                    onChange={(e) => handleGeneralChange('social_media', 'twitter', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'hero':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Ενότητα Hero</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ρυθμίσεις για την αρχική ενότητα (Hero) της ιστοσελίδας.
              </p>
            </div>
            
            <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Ορατότητα Ενότητας</span>
                <span className="ml-2 text-sm text-gray-500">(Εμφάνιση στην αρχική σελίδα)</span>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  name="hero_visible"
                  id="hero_visible"
                  checked={sectionVisibility.hero}
                  onChange={() => handleSectionVisibilityChange('hero')}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                />
                <label
                  htmlFor="hero_visible"
                  className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                    sectionVisibility.hero ? 'bg-green-400' : 'bg-gray-300'
                  }`}
                ></label>
              </div>
              <style jsx>{`
                .toggle-checkbox:checked {
                  right: 0;
                  border-color: #68D391;
                }
                .toggle-checkbox:checked + .toggle-label {
                  background-color: #68D391;
                }
                .toggle-checkbox {
                  right: 0;
                  transition: all 0.3s;
                  border-color: #CBD5E0;
                }
                .toggle-label {
                  transition: all 0.3s;
                }
              `}</style>
            </div>
            
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <label htmlFor="hero_title" className="block text-sm font-medium text-gray-700">
                  Τίτλος
                </label>
                <input
                  type="text"
                  id="hero_title"
                  value={heroContent.title}
                  onChange={(e) => handleHeroChange('title', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6">
                <label htmlFor="hero_subtitle" className="block text-sm font-medium text-gray-700">
                  Υπότιτλος
                </label>
                <textarea
                  id="hero_subtitle"
                  rows={3}
                  value={heroContent.subtitle}
                  onChange={(e) => handleHeroChange('subtitle', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="hero_cta_text" className="block text-sm font-medium text-gray-700">
                  Κείμενο Κουμπιού (CTA)
                </label>
                <input
                  type="text"
                  id="hero_cta_text"
                  value={heroContent.cta_text}
                  onChange={(e) => handleHeroChange('cta_text', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="hero_cta_link" className="block text-sm font-medium text-gray-700">
                  Σύνδεσμος Κουμπιού (URL ή #section)
                </label>
                <input
                  type="text"
                  id="hero_cta_link"
                  value={heroContent.cta_link}
                  onChange={(e) => handleHeroChange('cta_link', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6">
                <label htmlFor="hero_background" className="block text-sm font-medium text-gray-700">
                  Εικόνα Φόντου
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="text"
                    id="hero_background"
                    value={heroContent.background_url}
                    onChange={(e) => handleHeroChange('background_url', e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="https://example.com/background.jpg"
                  />
                  <button
                    type="button"
                    onClick={() => openMediaLibrary('hero_background')}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <SafeIcon icon={FiIcons.FiFolder} className="mr-1" />
                    Επιλογή
                  </button>
                </div>
                
                {heroContent.background_url && (
                  <div className="mt-2">
                    <div className="flex items-center mt-2">
                      <img 
                        src={heroContent.background_url} 
                        alt="Hero Background" 
                        className="h-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => handleHeroChange('background_url', '')}
                        className="ml-2 text-red-600 hover:text-red-900"
                      >
                        <SafeIcon icon={FiIcons.FiX} className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Μεταφόρτωση νέας εικόνας φόντου
                  </label>
                  <FileUploader 
                    onFileUploaded={(url) => handleFileUploaded(url)}
                    acceptedFileTypes="image/png,image/jpeg"
                    maxSizeMB={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'about':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Ενότητα "Σχετικά με εμάς"</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ρυθμίσεις για την ενότητα "Σχετικά με εμάς" της ιστοσελίδας.
              </p>
            </div>
            
            <div className="flex items-center justify-between mb-4 bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Ορατότητα Ενότητας</span>
                <span className="ml-2 text-sm text-gray-500">(Εμφάνιση στην αρχική σελίδα)</span>
              </div>
              <div className="relative inline-block w-12 mr-2 align-middle select-none">
                <input
                  type="checkbox"
                  name="about_visible"
                  id="about_visible"
                  checked={sectionVisibility.about}
                  onChange={() => handleSectionVisibilityChange('about')}
                  className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                />
                <label
                  htmlFor="about_visible"
                  className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                    sectionVisibility.about ? 'bg-green-400' : 'bg-gray-300'
                  }`}
                ></label>
              </div>
            </div>
            
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <label htmlFor="about_title" className="block text-sm font-medium text-gray-700">
                  Τίτλος
                </label>
                <input
                  type="text"
                  id="about_title"
                  value={aboutContent.title}
                  onChange={(e) => handleAboutChange('title', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6">
                <label htmlFor="about_content" className="block text-sm font-medium text-gray-700">
                  Περιεχόμενο
                </label>
                <textarea
                  id="about_content"
                  rows={5}
                  value={aboutContent.content}
                  onChange={(e) => handleAboutChange('content', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              
              <div className="col-span-6">
                <label htmlFor="about_image" className="block text-sm font-medium text-gray-700">
                  Εικόνα
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="text"
                    id="about_image"
                    value={aboutContent.image_url}
                    onChange={(e) => handleAboutChange('image_url', e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    placeholder="https://example.com/about.jpg"
                  />
                  <button
                    type="button"
                    onClick={() => openMediaLibrary('about_image')}
                    className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <SafeIcon icon={FiIcons.FiFolder} className="mr-1" />
                    Επιλογή
                  </button>
                </div>
                
                {aboutContent.image_url && (
                  <div className="mt-2">
                    <div className="flex items-center mt-2">
                      <img 
                        src={aboutContent.image_url} 
                        alt="About Image" 
                        className="h-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => handleAboutChange('image_url', '')}
                        className="ml-2 text-red-600 hover:text-red-900"
                      >
                        <SafeIcon icon={FiIcons.FiX} className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Μεταφόρτωση νέας εικόνας
                  </label>
                  <FileUploader 
                    onFileUploaded={(url) => handleFileUploaded(url)}
                    acceptedFileTypes="image/png,image/jpeg"
                    maxSizeMB={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'footer':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Περιεχόμενο Υποσέλιδου</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ρυθμίσεις για το υποσέλιδο της ιστοσελίδας.
              </p>
            </div>
            
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6">
                <label htmlFor="footer_copyright" className="block text-sm font-medium text-gray-700">
                  Κείμενο Πνευματικών Δικαιωμάτων
                </label>
                <input
                  type="text"
                  id="footer_copyright"
                  value={footerContent.copyright}
                  onChange={(e) => handleFooterChange('copyright', e.target.value)}
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Σύνδεσμοι Υποσέλιδου</h3>
              <p className="mt-1 text-sm text-gray-500">
                Σύνδεσμοι που θα εμφανίζονται στο υποσέλιδο.
              </p>
            </div>
            
            {footerContent.links.map((link, index) => (
              <div key={index} className="grid grid-cols-6 gap-6 border-t border-gray-200 pt-4">
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor={`link-text-${index}`} className="block text-sm font-medium text-gray-700">
                    Κείμενο
                  </label>
                  <input
                    type="text"
                    id={`link-text-${index}`}
                    value={link.text}
                    onChange={(e) => handleFooterLinkChange(index, 'text', e.target.value)}
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor={`link-url-${index}`} className="block text-sm font-medium text-gray-700">
                    URL
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      id={`link-url-${index}`}
                      value={link.url}
                      onChange={(e) => handleFooterLinkChange(index, 'url', e.target.value)}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => removeFooterLink(index)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <SafeIcon icon={FiIcons.FiX} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            <div>
              <button
                type="button"
                onClick={addFooterLink}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <SafeIcon icon={FiIcons.FiPlus} className="mr-2" />
                Προσθήκη Συνδέσμου
              </button>
            </div>
          </div>
        )
      
      case 'visibility':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Ορατότητα Ενοτήτων</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ρυθμίστε ποιες ενότητες θα εμφανίζονται στην αρχική σελίδα.
              </p>
            </div>
            
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <ul className="divide-y divide-gray-200">
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiHome} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα Hero</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="hero_toggle"
                      id="hero_toggle"
                      checked={sectionVisibility.hero}
                      onChange={() => handleSectionVisibilityChange('hero')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="hero_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.hero ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiLayers} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα Υπηρεσιών</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="services_toggle"
                      id="services_toggle"
                      checked={sectionVisibility.services}
                      onChange={() => handleSectionVisibilityChange('services')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="services_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.services ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiGrid} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα Λύσεων</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="solutions_toggle"
                      id="solutions_toggle"
                      checked={sectionVisibility.solutions}
                      onChange={() => handleSectionVisibilityChange('solutions')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="solutions_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.solutions ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiInfo} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα "Σχετικά με εμάς"</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="about_toggle"
                      id="about_toggle"
                      checked={sectionVisibility.about}
                      onChange={() => handleSectionVisibilityChange('about')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="about_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.about ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiMessageCircle} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα Μαρτυριών</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="testimonials_toggle"
                      id="testimonials_toggle"
                      checked={sectionVisibility.testimonials}
                      onChange={() => handleSectionVisibilityChange('testimonials')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="testimonials_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.testimonials ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon icon={FiIcons.FiMail} className="h-5 w-5 text-blue-500 mr-3" />
                    <span className="font-medium text-gray-800">Ενότητα Επικοινωνίας</span>
                  </div>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="contact_toggle"
                      id="contact_toggle"
                      checked={sectionVisibility.contact}
                      onChange={() => handleSectionVisibilityChange('contact')}
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    />
                    <label
                      htmlFor="contact_toggle"
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
                        sectionVisibility.contact ? 'bg-green-400' : 'bg-gray-300'
                      }`}
                    ></label>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }
  
  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Διαχείριση Περιεχομένου</h1>
      
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{success}</span>
        </div>
      )}
      
      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        ) : (
          <div>
            <div className="sm:hidden">
              <label htmlFor="content-tabs" className="sr-only">
                Επιλέξτε καρτέλα
              </label>
              <select
                id="content-tabs"
                name="content-tabs"
                className="block w-full focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="general">Γενικές Ρυθμίσεις</option>
                <option value="hero">Ενότητα Hero</option>
                <option value="about">Σχετικά με εμάς</option>
                <option value="footer">Υποσέλιδο</option>
                <option value="visibility">Ορατότητα Ενοτήτων</option>
              </select>
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('general')}
                    className={`${
                      activeTab === 'general'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Γενικές Ρυθμίσεις
                  </button>
                  <button
                    onClick={() => setActiveTab('hero')}
                    className={`${
                      activeTab === 'hero'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Ενότητα Hero
                  </button>
                  <button
                    onClick={() => setActiveTab('about')}
                    className={`${
                      activeTab === 'about'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Σχετικά με εμάς
                  </button>
                  <button
                    onClick={() => setActiveTab('footer')}
                    className={`${
                      activeTab === 'footer'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Υποσέλιδο
                  </button>
                  <button
                    onClick={() => setActiveTab('visibility')}
                    className={`${
                      activeTab === 'visibility'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Ορατότητα Ενοτήτων
                  </button>
                </nav>
              </div>
            </div>
            
            <div className="mt-6">
              {renderTab()}
              
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={saveContent}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {loading ? (
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2" />
                  ) : (
                    <SafeIcon icon={FiIcons.FiSave} className="mr-2" />
                  )}
                  Αποθήκευση
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Media Library Modal */}
      {isMediaLibraryOpen && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Βιβλιοθήκη Πολυμέσων
                    </h3>
                    
                    {loading ? (
                      <div className="flex justify-center my-8">
                        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 text-blue-500" />
                      </div>
                    ) : (
                      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {mediaItems.length > 0 ? (
                          mediaItems.map((item, index) => (
                            <div
                              key={index}
                              onClick={() => selectMedia(item.publicUrl)}
                              className="relative cursor-pointer border border-gray-200 rounded-lg overflow-hidden hover:border-blue-500 transition-colors"
                            >
                              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200">
                                <img
                                  src={item.publicUrl}
                                  alt={item.name}
                                  className="object-cover w-full h-full"
                                  onError={(e) => {
                                    e.target.onerror = null
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0Ljc1Ni0xLjMwMyAxLjA2MS0uMzkyLjI1NS0uOTA1LS4wNy0uNzc0LS40ODQuMTk4LS42MjUuNDU0LTEuMjEzLjc1OC0xLjc2NS43NTItMS4zNjQgMS40MzctMi4zMzkgMi40MzctMi4zMzlzMS42ODUuOTc1IDIuNDM3IDIuMzM5Yy43NTIgMS4zNjUgMS44MzUgMi4yODUgNC4wNjMgMS4zNjUgMi4wMjctLjgzNiAyLjc1Mi0uMzc1IDMuNS0uMzc1LjkxNSAwIC44NzUgMS41LTEuMDYzIDEuNzUtMS40MDUuMTgyLTQuNjgxIDEuNTM2LTIuOTk5LTIuMzQzLTEuMDAxIDEuMDIyLTEuNjU0IDEuMDc1LTIuODEyLjM0MmwtLjE4OC0uMTU4Yy0uOTgtLjgyNi0xLjI5Mi0uNzU1LTIuMDU2LjQ5OHptLS4zMzItMTAuMDY0Yy0xLjEwNSAwLTIgLjg5NS0yIDJzLjg5NSAyIDIgMiAyLS44OTUgMi0yLS44OTUtMi0yLTJ6Ii8+PC9zdmc+'
                                  }}
                                />
                              </div>
                              <div className="p-2 text-xs truncate">{item.name}</div>
                            </div>
                          ))
                        ) : (
                          <div className="col-span-full text-center py-12">
                            <SafeIcon icon={FiIcons.FiImage} className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Δεν υπάρχουν αρχεία</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Μεταφορτώστε εικόνες για να εμφανιστούν εδώ.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsMediaLibraryOpen(false)}
                >
                  Κλείσιμο
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContentManager